/**
 * PATH: packages/email-hub/src/feature/reader-header/command/MoreMenu.tsx
 * FILE_ID: EMAILHUB-READERHEADER-MORE-MENU-001
 * PURPOSE: Render the grouped long-tail action menu straight from the action registry.
 * ROLE IN ARCHITECTURE: Presentational leaf of the command band (plan "More menu and long-tail actions").
 * MAIN EXPORTS: ReaderMoreMenu.
 * NON-RESPONSIBILITIES: No API fetches before first paint; callers own handlers and busy state.
 * NOTES FOR FUTURE AI: Grouping/order comes ONLY from actions/registry.ts
 *   (readerOverflowGroup + stableOrder) — never hand-sort or usage-sort here.
 *   D269 D1: a row's caret/submenu is driven ONLY by `action.subActions`
 *   (built from the registry's contextActions in menus/overflowRowItems.ts
 *   + useReaderHeaderCommands.tsx) — never gate on `action.id === 'junk'`
 *   or any other hardcoded id here. Any future overflow action that gets a
 *   non-empty subActions list picks up the SAME split-button caret for free.
 */
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import { ChevronDown, Loader2 } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import { ContextMenu, ContextMenuItem } from '../../../_internal/components/ContextMenu'
import {
  READER_OVERFLOW_GROUP_LABELS,
  READER_OVERFLOW_GROUP_ORDER,
  readerOverflowGroup,
} from '../actions/overflowGroups'
import { getReaderAction } from '../actions/registry'
import type { ReaderOverflowGroup } from '../model/types'
import type { ReaderCommandAction } from './CommandBar'
import { shouldKeepMoreMenuOpenAfterAction } from './moreMenuDismiss'

export interface ReaderMoreMenuProps {
  actions: readonly ReaderCommandAction[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The More trigger — needed so a document-level dismiss ignores the toggle. */
  triggerRef?: RefObject<HTMLElement | null>
}

interface MenuGroup {
  group: ReaderOverflowGroup
  label: string | null
  actions: ReaderCommandAction[]
}

/** Pure: bucket actions into registry groups, registry-ordered, empty groups omitted. */
export function groupMoreMenuActions(
  actions: readonly ReaderCommandAction[],
): MenuGroup[] {
  const buckets = new Map<ReaderOverflowGroup, ReaderCommandAction[]>()
  for (const action of actions) {
    const group = readerOverflowGroup(action.id)
    const bucket = buckets.get(group)
    if (bucket) bucket.push(action)
    else buckets.set(group, [action])
  }
  return READER_OVERFLOW_GROUP_ORDER.filter((group) => buckets.has(group)).map(
    (group) => ({
      group,
      label: READER_OVERFLOW_GROUP_LABELS[group],
      actions: (buckets.get(group) as ReaderCommandAction[])
        .slice()
        .sort(
          (left, right) =>
            getReaderAction(left.id).stableOrder -
            getReaderAction(right.id).stableOrder,
        ),
    }),
  )
}

/**
 * One More-menu row. Plain actions render exactly as before (a single
 * button). Actions with resolved subActions (registry contextActions —
 * see menus/overflowRowItems.ts) render as a split control: the main face
 * keeps invoking `action.onClick` immediately on click (never demoted to
 * menu-only), and a trailing caret opens a small portalled panel for the
 * escalation rows. Mirrors JunkButton.tsx's split-control contract so the
 * behavior reads identically wherever the disposition is reachable from.
 */
function MoreMenuRow({
  action,
  danger,
  onActivate,
}: {
  action: ReaderCommandAction
  danger: boolean
  onActivate: () => void
}) {
  const [subMenuAt, setSubMenuAt] = useState<{ x: number; y: number } | null>(null)
  const caretRef = useRef<HTMLButtonElement>(null)
  const subActions = action.subActions ?? []
  const hasSubActions = subActions.length > 0
  const disabled = action.disabled || action.busy

  const rowClassName = cn(
    'flex min-h-9 flex-1 items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-accent',
    danger && 'text-destructive hover:bg-destructive/10',
    disabled && 'cursor-not-allowed opacity-50',
  )

  const openSubMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (disabled) return
    if (subMenuAt) {
      setSubMenuAt(null)
      return
    }
    const rect = caretRef.current?.getBoundingClientRect()
    setSubMenuAt({ x: rect?.left ?? 0, y: (rect?.bottom ?? 0) + 2 })
  }

  return (
    <div
      className={cn('flex items-stretch', hasSubActions && 'gap-px')}
      data-reader-overflow-row={action.id}
    >
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={() => {
          action.onClick()
          onActivate()
        }}
        className={rowClassName}
        data-reader-overflow-action={action.id}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          {action.busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            action.icon
          )}
        </span>
        <span>{action.label}</span>
      </button>
      {hasSubActions && (
        <button
          ref={caretRef}
          type="button"
          disabled={disabled}
          onClick={openSubMenu}
          title={`${action.label} options`}
          aria-haspopup="menu"
          aria-expanded={subMenuAt !== null}
          data-reader-overflow-caret={action.id}
          className={cn(
            'flex w-8 items-center justify-center rounded-sm touch-manipulation hover:bg-accent',
            danger && 'text-destructive hover:bg-destructive/10',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {subMenuAt && hasSubActions && (
        <ContextMenu
          x={subMenuAt.x}
          y={subMenuAt.y}
          label={`${action.label} options`}
          restoreFocusTo={caretRef.current}
          onClose={() => setSubMenuAt(null)}
        >
          {subActions.map((sub) => (
            <ContextMenuItem
              key={sub.id}
              variant={sub.dangerous ? 'danger' : 'default'}
              onClick={() => {
                sub.onClick()
                setSubMenuAt(null)
                onActivate()
              }}
            >
              {sub.label}
            </ContextMenuItem>
          ))}
        </ContextMenu>
      )}
    </div>
  )
}

export function ReaderMoreMenu({
  actions,
  open,
  onOpenChange,
  triggerRef,
}: ReaderMoreMenuProps) {
  const groups = useMemo(() => groupMoreMenuActions(actions), [actions])
  const menuRef = useRef<HTMLDivElement>(null)
  const [fixedPos, setFixedPos] = useState({ top: 0, right: 8 })

  useLayoutEffect(() => {
    if (!open) return
    const rect = triggerRef?.current?.getBoundingClientRect()
    if (!rect) return
    setFixedPos({
      top: rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    })
  }, [open, triggerRef])

  const fallbackTriggerRef = useRef<HTMLElement | null>(null)
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => onOpenChange(false), [menuRef, triggerRef ?? fallbackTriggerRef])

  if (!open) return null

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Email actions"
      style={{ top: fixedPos.top, right: fixedPos.right }}
      className="fixed z-[80] max-h-[min(70vh,520px)] min-w-[230px] overflow-y-auto rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-lg"
      data-testid="reader-more-menu"
    >
      {groups.map((menuGroup, index) => (
        <div
          key={menuGroup.group}
          role="group"
          aria-label={menuGroup.label ?? undefined}
          className={cn(
            index > 0 && 'mt-1 border-t border-border/60 pt-1',
            menuGroup.group === 'risk' && 'bg-destructive/[.04]',
          )}
          data-reader-menu-group={menuGroup.group}
        >
          {menuGroup.label && (
            <div className="px-3 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
              {menuGroup.label}
            </div>
          )}
          {menuGroup.actions.map((action) => (
            <MoreMenuRow
              key={action.id}
              action={action}
              danger={Boolean(action.dangerous || menuGroup.group === 'risk')}
              onActivate={() => {
                if (!shouldKeepMoreMenuOpenAfterAction(action.id)) {
                  onOpenChange(false)
                }
              }}
            />
          ))}
        </div>
      ))}
    </div>,
    document.body,
  )
}
